<template>
  <div class="relative text-sm px-4 pt-4 pb-4">
    <div class="flex flex-row items-center justify-between min-h-14 px-4">
      <h2 class="font-medium">
        {{ title }}
      </h2>
      <AM3Button
        v-if="!loading && items && items.length > 0"
        type="link"
        class="hover:underline"
        :text="items.length === 1 ? 'Edit field' : `Edit ${items.length} fields`"
        @click="emit('openEditor')"
      />
    </div>

    <AM3LoadingContent
      v-if="loading"
      :lines="3"
      size="sm"
      container-class="px-4 py-2"
    />

    <div
      v-else-if="displayedItems.length === 0"
      class="py-4 px-4 text-secondary"
    >
      {{ !items || items.length === 0 ? 'No custom fields' : 'No values set' }}
    </div>

    <div v-else>
      <div
        v-for="item in alwaysVisibleItems"
        :key="`pinned-${item.oid ?? item.title}`"
        class="flex flex-row items-center justify-between rounded-md w-full p-4"
      >
        <div class="flex-grow w-full text-left">
          <div class="text-secondary">
            {{ item.title }}
          </div>
          <div class="font-medium text-brand">
            {{ returnParsedData(item) || '-' }}
          </div>
        </div>
      </div>

      <AM3Collapsible
        v-if="overflowItems.length > 0"
        v-model="overflowCollapsed"
        header-class="px-4 hover:bg-brand-light"
      >
        <template #header>
          <span class="text-brand">
            {{ overflowCollapsed ? 'Show more' : 'Show fewer' }}
          </span>
        </template>

        <div
          v-for="item in overflowItems"
          :key="`overflow-${item.oid ?? item.title}`"
          class="flex flex-row items-center justify-between rounded-md w-full p-4"
        >
          <div class="flex-grow w-full text-left">
            <div class="text-secondary">
              {{ item.title }}
            </div>
            <div class="font-medium text-brand">
              {{ returnParsedData(item) || '-' }}
            </div>
          </div>
        </div>
      </AM3Collapsible>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import AM3Collapsible from '@/components/am3-generic/layout/AM3Collapsible.vue'
import AM3LoadingContent from '@/components/am3-generic/base/AM3LoadingContent.vue'

interface InputOption {
  name: string
  value?: string
  selected?: boolean
}

interface InputField {
  type: string
  name: string
  placeholder?: string
  savedData: any
  options?: InputOption[]
}

interface ListItem {
  icon?: string
  title: string
  modifiable: boolean
  outputAs: string
  input: InputField
  oid?: string
}

interface Props {
  title?: string
  items: ListItem[]
  inlineLimit?: number
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  items: () => [],
  inlineLimit: 4,
  loading: false,
})

const emit = defineEmits(['openEditor'])

const overflowCollapsed = ref(true)

const hasValue = (field: ListItem) => {
  const saved = field.input?.savedData
  if (Array.isArray(saved)) return saved.length > 0
  return saved !== null && saved !== undefined && saved !== ''
}

const displayedItems = computed(() => props.items.filter(hasValue))

// Show everything inline when there are ≤inlineLimit values; otherwise pin the
// first `inlineLimit` and tuck the rest behind a "View N more" toggle.
const alwaysVisibleItems = computed(() =>
  displayedItems.value.length > props.inlineLimit
    ? displayedItems.value.slice(0, props.inlineLimit)
    : displayedItems.value,
)

const overflowItems = computed(() =>
  displayedItems.value.length > props.inlineLimit
    ? displayedItems.value.slice(props.inlineLimit)
    : [],
)

const returnPrettyDate = (field: ListItem) => {
  if (!field.input.savedData) return null
  try {
    return dayjs(field.input.savedData).format('MMM D, YYYY')
  }
  catch {
    return null
  }
}

const returnPrettyString = (field: ListItem) => {
  const saved = field.input.savedData
  return saved != null ? String(saved) : ''
}

const returnPrettySelectData = (field: ListItem) => {
  const { savedData, options } = field.input
  const selectedValue = savedData && savedData[0]?.length > 0 ? savedData[0] : ''
  return options?.some(option => option.name === selectedValue) ? String(selectedValue) : ''
}

const returnPrettyMultiselectData = (field: ListItem) => {
  const { savedData, options } = field.input
  return options
    ?.filter(option => savedData?.includes(option.name))
    .map(option => option.name)
    .join(', ') || ''
}

const returnParsedData = (field: ListItem) => {
  switch (field.outputAs) {
    case 'Date': return returnPrettyDate(field)
    case 'Select': return returnPrettySelectData(field)
    case 'Options':
    case 'Multiselect': return returnPrettyMultiselectData(field)
    default: return returnPrettyString(field)
  }
}
</script>
